import { FocusEvent, useCallback, useMemo, useRef } from "react";
import {
  Control,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
} from "react-hook-form";
import { SxProps } from "@mui/system";

import { FormAutocomplete } from "../FormAutocomplete";
import { getOptionsForModelYear } from "./utils";

type Props<TValue extends FieldValues> = {
  control: Control<TValue>;
  name?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any;
  label?: React.ReactNode;
  placeholder?: string;
  tooltip?: string;
  disabled?: boolean;
  inputStyle?: SxProps;
  optional?: boolean;
  fontSize?: number;
  labelFontSize?: number;
};

const FormModelYearAutocomplete = <TValue extends FieldValues>({
  control,
  name = "modelYear",
  defaultValue = "",
  label = "Model Year",
  placeholder = "ex. 2010",
  tooltip = "Indicate the model year for the home. If no model year is known, then use the construction year.",
  disabled = false,
  inputStyle = {
    padding: "0px !important",
  },
  optional = false,
  fontSize,
  labelFontSize,
  ...props
}: Props<TValue>) => {
  const options = useMemo(() => getOptionsForModelYear(), []);
  const formContext = useFormContext<TValue>();

  const dropdownSelectionRef = useRef(false);
  const isTypingRef = useRef(false);

  const handleInputChange = useCallback(() => {
    isTypingRef.current = true;
  }, []);

  const handleChange = useCallback(
    (
      _event: React.SyntheticEvent,
      value: { label: string; value: number } | string | null
    ) => {
      if (value && typeof value === "object") {
        dropdownSelectionRef.current = true;
        isTypingRef.current = false;
      }
    },
    []
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const fieldName = name as Path<TValue>;
      const rawInputValue = event.target.value.trim();
      const currentValue = formContext.getValues(fieldName);

      if (dropdownSelectionRef.current) {
        dropdownSelectionRef.current = false;
        isTypingRef.current = false;

        return;
      }

      if (currentValue?.label === rawInputValue && !isTypingRef.current) {
        return;
      }

      isTypingRef.current = false;

      if (!rawInputValue) {
        formContext.setValue(
          fieldName,
          "" as PathValue<TValue, typeof fieldName>,
          { shouldDirty: true, shouldValidate: true }
        );

        return;
      }

      const matchingOption = options.find((option) => {
        const optionLabel = option.label.trim();

        return (
          optionLabel.toLowerCase() === rawInputValue.toLowerCase() ||
          option.value?.toString() === rawInputValue
        );
      });

      if (matchingOption) {
        if (currentValue?.value === matchingOption.value) {
          return;
        }

        formContext.clearErrors?.(fieldName);
        formContext.setValue(
          fieldName,
          matchingOption as PathValue<TValue, typeof fieldName>,
          { shouldDirty: true, shouldValidate: true }
        );

        return;
      }

      const invalidValue = { invalid: true } as PathValue<
        TValue,
        typeof fieldName
      >;

      formContext.setValue(fieldName, invalidValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [formContext, name, options]
  );

  return (
    <FormAutocomplete
      {...props}
      id="model-year"
      dataTestId="model-year-input"
      name={name}
      options={options}
      control={control}
      label={label}
      tooltip={tooltip}
      placeholder={placeholder}
      inputStyle={inputStyle}
      defaultValue={defaultValue}
      // eslint-disable-next-line
      // @ts-ignore
      freeSolo
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlurChange={handleBlur}
      inputProps={{
        sx: {
          padding: "0 13px 0 14px !important",
        },
      }}
      optional={optional}
      fontSize={fontSize}
      labelFontSize={labelFontSize}
      disabled={disabled}
    />
  );
};

export { FormModelYearAutocomplete };
